import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  tableCellClasses,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import ApiErrorMessage from '../Global/ApiErrorMessage';
import { NOT_AVAILABLE } from '../Global/Constants';
import Loader from '../Global/Loader';
import CallCenterService from '../services/CallCenterService';
import { isValidValue } from '../../utils/CommonFunction';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';

const { GetAllAssignableStatuses, UpdateStatus } = CallCenterService;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    padding: '6px',
    height: '42.5px',
    fontWeight: 500,
    textTransform: 'uppercase',
    color: '#0d4689'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    padding: '4px 6px',
    height: '42.5px'
  }
}));

function UpdateCallCenter() {
  const navigate = useNavigate();
  const location = useLocation();
  const { leadesItem } = location?.state || {};
  const [openFollowUp, setOpenFollowUp] = useState({ status: false, indexVal: null });
  const [updateStatusList, setUpdateStatusList] = useState([]);
  const [selectedItemList, setSelectedItemList] = useState([]);
  const [openApiError, setOpenApiError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [validationMsg, setValidationMsg] = useState('');
  const [openLoader, setOpenLoader] = useState(false);

  const { LoginData } = useLoginSelector();

  const handleStatus = (e, index) => {
    const selectedValue = e.target.value;
    if (JSON.parse(selectedValue).stageStatusName === 'Follow Up') {
      setOpenFollowUp({ status: true, indexVal: index });
    }
    const prevArray = selectedItemList;
    prevArray[index].status = selectedValue;
    setSelectedItemList([...prevArray]);
  };

  const handleFollowUpSubmit = (e, indexVal) => {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - 1);
    if (selectedItemList[indexVal].followUpAppointmentDetails.appointmentDate === '') {
      setDialogOpen(true);
      setValidationMsg('Please enter appointment date');
      return;
    } else if (
      currentDate > new Date(selectedItemList[indexVal].followUpAppointmentDetails.appointmentDate)
    ) {
      setDialogOpen(true);
      setValidationMsg('Please enter valid appointment date');
      return;
    }

    if (selectedItemList[indexVal].followUpAppointmentDetails.appointmentTime === '') {
      setDialogOpen(true);
      setValidationMsg('Please enter appointment time');
      return;
    }

    const prevArray = selectedItemList;
    prevArray[indexVal].followUpAppointmentDetails.isSubmitClicked = true;
    setSelectedItemList([...prevArray]);
    setOpenFollowUp({ status: false, indexVal: null });
  };

  const handleFollowUpCloseIcon = (e) => {
    const prevArray = selectedItemList;
    const followUpDetails = prevArray?.[openFollowUp.indexVal]?.followUpAppointmentDetails;

    if (
      isValidValue(followUpDetails?.appointmentDate) &&
      isValidValue(followUpDetails?.appointmentTime) &&
      followUpDetails?.isSubmitClicked
    ) {
      setOpenFollowUp({ status: false, indexVal: null });
    } else {
      prevArray[openFollowUp.indexVal].status = ' ';
      setSelectedItemList([...prevArray]);
      setOpenFollowUp({ status: false, indexVal: null });
    }
  };

  const handleUpdate = async (e) => {
    const objArray = [];
    selectedItemList.forEach((item) => {
      if (item.status !== ' ' && JSON.parse(item.status).stageStatusName === 'Follow Up') {
        const obj = {
          leadId: item.leadId,
          stageId: item.stageId,
          stageStatusId: JSON.parse(item.status).stageStatusId,
          userId: LoginData?.userId,
          followUpAppointmentDetails: {
            appointmentDate: item.followUpAppointmentDetails.appointmentDate,
            appointmentTime: item.followUpAppointmentDetails.appointmentTime
          },
          comments: item.comments
        };
        objArray.push(obj);
      } else if (item.status !== ' ') {
        const obj = {
          leadId: item.leadId,
          stageId: item.stageId,
          stageStatusId: JSON.parse(item.status).stageStatusId,
          userId: LoginData?.userId,
          comments: item.comments
        };
        objArray.push(obj);
      }
    });

    if (objArray.length === selectedItemList.length) {
      const UpdateStatusApi = await UpdateStatus(objArray, LoginData?.jwt);
      if (UpdateStatusApi.status === 'success') {
        if (
          selectedItemList[0].status !== ' ' &&
          JSON.parse(selectedItemList[0].status).stageStatusName.toLowerCase() === 'submitted'
        ) {
          setSelectedItemList([]);
          navigate('/leads');
        } else {
          setSelectedItemList([]);
          navigate('/leads/activity', { state: location?.state });
        }
      } else {
        setOpenApiError(true);
        setErrorMessage('Something went wrong !');
      }
    } else {
      setDialogOpen(true);
      setValidationMsg('Please select status');
    }
  };

  useEffect(async () => {
    const GetAllAssignableStatusesApi = await GetAllAssignableStatuses(
      leadesItem[0]?.stageId,
      LoginData?.jwt
    );
    if (GetAllAssignableStatusesApi.status === 'success') {
      GetAllAssignableStatusesApi.data.forEach((item) => {
        item.leadStatus = true;
        if (
          leadesItem[0]?.leadStatus?.toLowerCase() !== 'completed' &&
          item.stageStatusName.toLowerCase() === 'submitted'
        ) {
          item.leadStatus = false;
        }
      });

      const newArray = GetAllAssignableStatusesApi.data.filter(
        (item) => item.stageStatusName.toLowerCase() !== 'completed'
      );
      setUpdateStatusList(newArray);
    }

    if (leadesItem && leadesItem.length > 0) {
      leadesItem.forEach((item, index) => {
        item.status = ' ';
        item.comments = '';
        item.followUpAppointmentDetails = {
          appointmentDate: '',
          appointmentTime: '',
          isSubmitClicked: false
        };
      });
      setSelectedItemList(leadesItem);
    } else {
      setSelectedItemList([]);
    }
  }, [LoginData, leadesItem]);

  useEffect(() => {
    setOpenLoader(true);
    if (selectedItemList.length > 0) {
      setOpenLoader(false);
    }
  }, [selectedItemList]);
  return (
    <Page title="Partner Management System || Call Center">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Update Lead Status" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Box className="tableBox" mb={3}>
            <TableContainer sx={{ minWidth: 800, overflow: 'auto', background: 'white' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="">Lead Name</StyledTableCell>
                    <StyledTableCell align="">Last Status</StyledTableCell>
                    <StyledTableCell align="">New Status</StyledTableCell>
                    <StyledTableCell align="">Comments</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedItemList.length > 0
                    ? selectedItemList.map((item, index) => (
                        <StyledTableRow>
                          <StyledTableCell align="">
                            {item.customerName ? item.customerName : `${NOT_AVAILABLE}`}
                          </StyledTableCell>
                          <StyledTableCell align="">
                            {item.leadStatus ? item.leadStatus : `${NOT_AVAILABLE}`}
                          </StyledTableCell>
                          <StyledTableCell align="">
                            <Select
                              fullWidth
                              inputProps={{
                                className: `textInput textInputTable customSelectBox dissabledMenu${item.status}`
                              }}
                              value={item.status}
                              onChange={(e) => {
                                handleStatus(e, index);
                              }}
                              sx={{ minWidth: '200px' }}
                            >
                              <MenuItem value=" " disabled>
                                Select Status
                              </MenuItem>

                              {updateStatusList.length > 0
                                ? updateStatusList.map((item1, index1) => (
                                    <MenuItem
                                      disabled={!item1.leadStatus}
                                      value={JSON.stringify(item1)}
                                      key={index1}
                                    >
                                      {item1.stageStatusName}
                                    </MenuItem>
                                  ))
                                : ''}
                            </Select>
                          </StyledTableCell>
                          <StyledTableCell align="">
                            <TextField
                              fullWidth
                              inputProps={{
                                className: 'textInput textInputTable'
                              }}
                              placeholder="Write Your Comments"
                              sx={{ minWidth: '200px' }}
                              value={item.comments}
                              onChange={(e) => {
                                const prevArray = selectedItemList;
                                prevArray[index].comments = e.target.value;
                                setSelectedItemList([...prevArray]);
                              }}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    : ''}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box className="" textAlign="right">
            <Button variant="contained" className="mainBtn" onClick={(e) => handleUpdate(e)}>
              Update
            </Button>
          </Box>
          <Dialog
            className="PopupBox"
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            open={openFollowUp.status}
            fullWidth
          >
            <DialogTitle className="popupTitle">
              <Typography variant="h6">Follow Up</Typography>
              <IconButton
                onClick={(e) => {
                  handleFollowUpCloseIcon(e);
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box>
                    <FormControl required fullWidth className="formGroup">
                      <Typography variant="body1" className="formLabel">
                        Date
                      </Typography>
                      <TextField
                        type="date"
                        id="Date"
                        inputProps={{ className: 'textInput' }}
                        fullWidth
                        placeholder="Date"
                        value={
                          selectedItemList[openFollowUp.indexVal]?.followUpAppointmentDetails
                            .appointmentDate
                        }
                        onChange={(e) => {
                          const prevArray = selectedItemList;
                          prevArray[
                            openFollowUp.indexVal
                          ].followUpAppointmentDetails.appointmentDate = e.target.value;
                          setSelectedItemList([...prevArray]);
                        }}
                      />
                    </FormControl>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <FormControl required fullWidth className="formGroup">
                      <Typography variant="body1" className="formLabel">
                        Time
                      </Typography>
                      <TextField
                        type="time"
                        id="time"
                        inputProps={{ className: 'textInput' }}
                        fullWidth
                        placeholder="Time"
                        value={
                          selectedItemList[openFollowUp.indexVal]?.followUpAppointmentDetails
                            .appointmentTime
                        }
                        onChange={(e) => {
                          const prevArray = selectedItemList;
                          prevArray[
                            openFollowUp.indexVal
                          ].followUpAppointmentDetails.appointmentTime = e.target.value;
                          setSelectedItemList([...prevArray]);
                        }}
                      />
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={(e) => {
                  handleFollowUpSubmit(e, openFollowUp.indexVal);
                }}
                variant="contained"
                className="mainBtn"
              >
                Submit
              </Button>
            </DialogActions>
          </Dialog>

          <ApiErrorMessage
            openApiError={openApiError}
            setOpenApiError={setOpenApiError}
            errorMessage={errorMessage}
          />

          <Loader openLoader={openLoader} />

          <Dialog
            open={dialogOpen}
            onClose={(e) => setDialogOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogContent>
              <DialogContentText id="alert-dialog-description">{validationMsg}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={(e) => setDialogOpen(false)}>Retry</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </Page>
  );
}
export default UpdateCallCenter;
